// React component

import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMaintenance } from "@/context/MaintenanceContext";
import { useEffect } from "react";

export const MaintenanceOverlay = () => {
  const { isInMaintenance, setIsInMaintenance } = useMaintenance();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  // Show maintenance overlay only on development path
  useEffect(() => {
    if (pathname.includes("development")) {
      setIsInMaintenance(true);
    }
  }, [pathname, setIsInMaintenance]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default Link behavior

    // First, trigger the exit animation
    await new Promise((resolve) => setTimeout(resolve, 500)); // Match the exit animation duration

    // Then navigate programmatically
    navigate("/");

    // Finally, remove the maintenance state
    setTimeout(() => {
      setIsInMaintenance(false);
    }, 100);
  };

  if (!pathname.includes("development")) return null;

  return (
    <AnimatePresence data-oid="_hde-i3">
      {isInMaintenance && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md bg-black/30"
          data-oid="hf.c-l8"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              delay: 0.2,
            }}
            className="relative px-5 py-6 sm:p-8 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 shadow-2xl border border-white/10 max-w-sm sm:max-w-md w-full mx-4"
            data-oid="r9feyn5"
          >
            <motion.div
              animate={{
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 opacity-20 blur-xl"
              data-oid="ow.8:k8"
            />

            <div className="relative" data-oid="3zrr8gb">
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="mb-4 sm:mb-6"
                data-oid="76kdg1c"
              >
                <span
                  className="block w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 text-4xl sm:text-6xl"
                  data-oid="mdkw.ip"
                >
                  🛠️
                </span>
              </motion.div>

              <h2
                className="text-2xl sm:text-3xl font-bold text-center text-white mb-3 sm:mb-4"
                data-oid="1jtlkvt"
              >
                In Maintenance
              </h2>

              <p
                className="text-sm sm:text-base text-gray-200 text-center mb-4 sm:mb-6 px-2"
                data-oid="n3xs8_7"
              >
                We&apos;re currently performing some updates to improve your
                experience. Please check back soon!
              </p>

              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="flex justify-center space-x-2 mb-5 sm:mb-8"
                data-oid="mpt1h.g"
              >
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-white/70 animate-pulse"
                    style={{
                      animationDelay: `${i * 0.2}s`,
                    }}
                    data-oid="8da1u9z"
                  />
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex justify-center"
                data-oid="higf:u4"
              >
                <Link to="/" onClick={handleClick} data-oid="8o4:xli">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-5 py-2 sm:px-6 sm:py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm sm:text-base font-medium 
                              hover:from-purple-700 hover:to-pink-700 transition-colors duration-200 
                              shadow-lg hover:shadow-purple-500/25"
                    data-oid="75rj_1r"
                  >
                    Go Back Home
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MaintenanceOverlay;
